import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { goldGlow, silverGlow, bronzeGlow, glow } from '../styles/animations';

// Styled component for the rank badge
const BadgeContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.textPrimary};
  position: relative;
  z-index: 2;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 80px;
    height: 80px;
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
  
  /* Rank-specific styling */
  ${({ rank, theme }) => {
    switch (rank) {
      case 1:
        return `
          background: linear-gradient(135deg, #FFD700 0%, #FFC800 100%);
          color: #000;
          border: 2px solid #FFD700;
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.7);
        `;
      case 2:
        return `
          background: linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%);
          color: #000;
          border: 2px solid #C0C0C0;
          box-shadow: 0 0 15px rgba(192, 192, 192, 0.7);
        `;
      case 3:
        return `
          background: linear-gradient(135deg, #CD7F32 0%, #B06728 100%);
          color: #000;
          border: 2px solid #CD7F32;
          box-shadow: 0 0 15px rgba(205, 127, 50, 0.7);
        `;
      default:
        return `
          background: linear-gradient(135deg, #555555 0%, #333333 100%);
          color: ${theme.colors.textPrimary};
          border: 2px solid #555555;
          box-shadow: 0 0 10px rgba(85, 85, 85, 0.5);
        `;
    }
  }}
  
  &:before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    background: transparent;
    z-index: -1;
    
    ${({ rank }) => {
      switch (rank) {
        case 1:
          return `
            border: 2px solid rgba(255, 215, 0, 0.3);
          `;
        case 2:
          return `
            border: 2px solid rgba(192, 192, 192, 0.3);
          `;
        case 3:
          return `
            border: 2px solid rgba(205, 127, 50, 0.3);
          `;
        default:
          return `
            border: 2px solid rgba(85, 85, 85, 0.3);
          `;
      }
    }}
  }
`;

// Suffix for rank (st, nd, rd, th)
const getRankSuffix = (rank) => {
  if (rank === 1) return 'st';
  if (rank === 2) return 'nd';
  if (rank === 3) return 'rd';
  return 'th';
};

// Get animation variant based on rank
const getAnimationVariant = (rank) => {
  switch (rank) {
    case 1:
      return goldGlow;
    case 2:
      return silverGlow;
    case 3:
      return bronzeGlow;
    default:
      return glow;
  }
};

/**
 * RankBadge Component
 * Displays a circular badge with the team's rank (1st, 2nd, 3rd, 4th)
 * 
 * @param {Object} props - Component props
 * @param {number} props.rank - The team's rank (1-4)
 * @param {boolean} props.animate - Whether to animate the badge
 */
const RankBadge = ({ rank, animate = true }) => {
  const animationVariant = getAnimationVariant(rank);
  
  return (
    <BadgeContainer
      rank={rank}
      initial={animate ? "hidden" : "visible"}
      animate="visible"
      variants={animationVariant}
    >
      {rank}<sup>{getRankSuffix(rank)}</sup>
    </BadgeContainer>
  );
};

export default RankBadge;